import { Dispatch } from 'react-redux';
import { StoreState } from '../../../reducers';
import {
	Progress,
	DownloadMode,
	DownloadManagerItemState,
	DownloadStatus,
	DownloadGroupStatus,
	DownloadGroupName,
	SelectedDownloadsManagerItemState
} from '../reducer';
import { Api } from '../../../typings/api';
import Utils, { downloadsFolder, tempFolder } from '../../../utils';
import NativeApi from '../../../utils/nativeApi';
import { DriveItemInfo } from '../../DriveItemList/reducer';
import { createNotification } from '../../NotificationBar/actions';
import {
	DownloadManagerActionKeys,
	LocalStorageDownloadInfos,
	AddItemAction,
	UpdateStatusAction,
	UpdateDownloadAction,
	SetRequestIdAction,
	RemoveDownloadAction,
	SetDeleteAllStatusAction,
	InitPreviousDownloadGroupsAction,
	SetDownloadGroupStatusAction,
	CloseDownloadGroupToastAction,
	AddSelectedDownloadItemAction,
	RemovePendingSelectedDownloadAction,
	AddDownloadInProgressItemAction,
	RemoveDownloadInProgressItemAction,
	AddDownloadInProgressDependencyAction,
	RemoveDownloadInProgressDependencyAction,
	SetIsMarkingItemForDeletionAction,
	ResetDownloadsAction,
} from './typings';
import IndexedDBManager from '../../../utils/indexedDB/manager';
const Q = require('q');

interface StoredDownloadGroups {
	all: DownloadGroupStatus;
	important: DownloadGroupStatus;
	selected: DownloadGroupStatus;
}

const initPreviousDownloadGroups = (
	downloadAllStatus: DownloadGroupStatus,
	downloadImportantStatus: DownloadGroupStatus,
	downloadSelectedStatus: DownloadGroupStatus
): InitPreviousDownloadGroupsAction => {
	return {
		type: DownloadManagerActionKeys.INIT_PREVIOUS_DOWNLOAD_GROUPS,
		downloadGroupStatus: downloadAllStatus,
		downloadImportantStatus,
		downloadSelectedStatus,
	};
};

const setDownloadGroupStatus = (group: DownloadGroupName, status: DownloadGroupStatus): SetDownloadGroupStatusAction => {
	IndexedDBManager.setDownloadGroups(group, status).then();

	return {
		type: DownloadManagerActionKeys.SET_DOWNLOAD_GROUP_STATUS,
		group,
		status,
	};
};

const startDownloadGroup = (downloadGroupName: DownloadGroupName) => (dispatch: Dispatch<StoreState>): void => {
	dispatch(setDownloadGroupStatus(downloadGroupName, DownloadGroupStatus.Started));
};

const finishDownloadGroup = (downloadGroupName: DownloadGroupName) => (dispatch: Dispatch<StoreState>): void => {
	dispatch(setDownloadGroupStatus(downloadGroupName, DownloadGroupStatus.Finished));
};

const setIsDeletingAllStatus = (isDeletingAll: boolean): SetDeleteAllStatusAction => ({
	type: DownloadManagerActionKeys.SET_DELETE_ALL_STATUS,
	isDeletingAll
});

const setIsMarkingItemForDeletion = (isMarkingItemForDeletion: boolean): SetIsMarkingItemForDeletionAction => ({
	type: DownloadManagerActionKeys.SET_IS_MARKING_ITEM_FOR_DELETION,
	isMarkingItemForDeletion
});

const loadPreviousDownloads = () => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	const previousDownloads = await IndexedDBManager.getDownloads();
	const previousDownloadGroups = await IndexedDBManager.getDownloadGroups();
	const previousPendingSelectedDownloads = await IndexedDBManager.getPendingSelectedDownloads();

	if (previousDownloads && previousDownloads.length > 0) {
		dispatch({
			type: DownloadManagerActionKeys.INIT_DOWNLOADS,
			downloads: previousDownloads,
		});
	}

	if (previousDownloadGroups) {
		dispatch(initPreviousDownloadGroups(previousDownloadGroups.all, previousDownloadGroups.important, previousDownloadGroups.selected));
	}

	if (previousPendingSelectedDownloads && previousPendingSelectedDownloads.length > 0) {
		dispatch({
			type: DownloadManagerActionKeys.INIT_SELECTED_DOWNLOADS,
			pendingSelectedDownloads: previousPendingSelectedDownloads,
		});
	}
};

const addItemInManager = (driveItem: Api.DriveItem, mode: DownloadMode, dependencies: number[], dependents: number[]): AddItemAction => ({
	type: DownloadManagerActionKeys.ADD_ITEM,
	id: driveItem.id,
	mode,
	dependencies,
	dependents,
	version: driveItem.version,
});

const addSelectedDownloadItemInManager = (driveItemId: number): AddSelectedDownloadItemAction => ({
	type: DownloadManagerActionKeys.ADD_SELECTED_DOWNLOAD_ITEM,
	driveItemId,
});

const addDownloadInProgressItem = (driveItemId: number): AddDownloadInProgressItemAction => ({
	type: DownloadManagerActionKeys.ADD_DOWNLOAD_IN_PROGRESS_ITEM,
	driveItemId,
});

const addDownloadInProgressDependency = (dependencyId: number): AddDownloadInProgressDependencyAction => ({
	type: DownloadManagerActionKeys.ADD_DOWNLOAD_IN_PROGRESS_DEPENDENCY,
	dependencyId,
});

const updateDownloadStatusInManager = (id: number, status: DownloadStatus): UpdateStatusAction => ({
	type: DownloadManagerActionKeys.UPDATE_STATUS,
	id,
	status
});

const updateDownloadInManager = (driveItem: Api.DriveItem, mode: DownloadMode, dependencies: number[], dependents: number[]): UpdateDownloadAction => ({
	type: DownloadManagerActionKeys.UPDATE_DOWNLOAD,
	id: driveItem.id,
	mode,
	dependencies,
	dependents,
	version: driveItem.version,
});

const setRequestId = (id: number, requestId: number, isUpdating: boolean): SetRequestIdAction => ({
	type: DownloadManagerActionKeys.SET_REQUEST_ID,
	id,
	requestId,
	isUpdating
});

const removeDownloadFromManager = (id: number): RemoveDownloadAction => ({
	type: DownloadManagerActionKeys.REMOVE_DOWNLOAD,
	id
});

const removePendingSelectedDownloadFromManager = (driveItemId: number): RemovePendingSelectedDownloadAction => ({
	type: DownloadManagerActionKeys.REMOVE_PENDING_SELECTED_DOWNLOAD_ITEM,
	driveItemId
});

const removeDownloadInProgressItem = (driveItemId: number): RemoveDownloadInProgressItemAction => ({
	type: DownloadManagerActionKeys.REMOVE_DOWNLOAD_IN_PROGRESS_ITEM,
	driveItemId,
});

const removeDownloadInProgressDependency = (dependencyId: number): RemoveDownloadInProgressDependencyAction => ({
	type: DownloadManagerActionKeys.REMOVE_DOWNLOAD_IN_PROGRESS_DEPENDENCY,
	dependencyId,
});

const resetTotalSelectedDownloadsInManager = () => ({
	type: DownloadManagerActionKeys.RESET_TOTAL_SELECTED_DOWNLOAD_ITEMS,
});

const resetPendingSelectedDownloadsInManager = () => ({
	type: DownloadManagerActionKeys.RESET_PENDING_SELECTED_DOWNLOAD_ITEMS,
});

const resetDownloads = (): ResetDownloadsAction => ({
	type: DownloadManagerActionKeys.RESET_DOWNLOADS,
});

const addItem = (driveItem: Api.DriveItem, downloadMode: DownloadMode, dependenciesIds: number[], dependentsIds: number[]) =>
	(dispatch: Dispatch<StoreState>): void => {
		dispatch(addItemInManager(driveItem, downloadMode, dependenciesIds, dependentsIds));
		dispatch(updateDownloadsInIndexedDB());
	};

const updateDownloadStatus = (id: number, status: DownloadStatus) =>
	async (dispatch: Dispatch<StoreState>): Promise<void> => {
		await dispatch(updateDownloadStatusInManager(id, status));
		await dispatch(updateDownloadsInIndexedDB());
	};

const updateDownload = (driveItem: Api.DriveItem, downloadMode: DownloadMode, dependenciesIds: number[], dependentsIds: number[]) =>
	(dispatch: Dispatch<StoreState>): void => {
		dispatch(updateDownloadInManager(driveItem, downloadMode, dependenciesIds, dependentsIds));
		dispatch(updateDownloadsInIndexedDB());
	};

const removeDownloadItem = (id: number) =>
	(dispatch: Dispatch<StoreState>): void => {
		dispatch(removeDownloadFromManager(id));
		dispatch(updateDownloadsInIndexedDB());
	};

const updateDownloadsInIndexedDB = () => (
	dispatch: Dispatch<StoreState>,
	getState: () => StoreState
): Q.Promise<void> => {
	return Q.Promise((resolve) => {
		let downloadsToSave: LocalStorageDownloadInfos[] = [];
		const { downloadManager } = getState();
		const { items } = downloadManager;

		if (items) {
			Object.keys(items).forEach(key => {
				const id = parseInt(key, 10);
				const item: DownloadManagerItemState = items[id];

				if (item && item.mode !== DownloadMode.Temp) {
					downloadsToSave.push({
						id,
						version: item.version,
						mode: item.mode,
						status: item.status,
						dependencies: item.dependencies,
						dependents: item.dependents,
					});
				}
			});
			IndexedDBManager.setDownloads(downloadsToSave).then();
			resolve();
		} else {
			console.error('updateDownloadsInIndexedDB: items is undefined');
			resolve();
		}
	});
};

const addSelectedDownloadItem = (id: number) =>
	(dispatch: Dispatch<StoreState>): void => {
		dispatch(addSelectedDownloadItemInManager(id));
		dispatch(updatePendingSelectedDownloadsInIndexedDB()).then();
	};

const resetSelectedDownloadItems = () =>
	(dispatch: Dispatch<StoreState>): void => {
		dispatch(finishDownloadGroup(DownloadGroupName.SELECTED));
		dispatch(resetPendingSelectedDownloadsInManager());
		dispatch(updatePendingSelectedDownloadsInIndexedDB()).then();
		dispatch(resetTotalSelectedDownloadsInManager());
	};

const removePendingSelectedDownloadItem = (id: number) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	await dispatch(removePendingSelectedDownloadFromManager(id));

	await dispatch(updatePendingSelectedDownloadsInIndexedDB());
};

const updatePendingSelectedDownloadsInIndexedDB = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	let pendingSelectedDownloadsToSave: SelectedDownloadsManagerItemState[] = [];
	const { downloadManager } = getState();
	const { pendingSelectedDownloads } = downloadManager;

	Object.keys(pendingSelectedDownloads).forEach(key => {
		const id = parseInt(key, 10);
		const item: SelectedDownloadsManagerItemState = pendingSelectedDownloads[id];

		pendingSelectedDownloadsToSave.push({
			driveItemId: item.driveItemId,
		});
	});

	await IndexedDBManager.setPendingSelectedDownloads(pendingSelectedDownloadsToSave);
};

interface DependencyInfo {
	fromDownloadManager: DownloadManagerItemState;
	fromDriveList: DriveItemInfo;
}

const removeDependentFromDependencies = (driveItem: Api.DriveItem) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
		const driveItemInDownloadManager = getState().downloadManager.items[driveItem.id];
		const dependenciesIds = driveItemInDownloadManager.dependencies;
		let dependenciesInfo: {[key: number]: DependencyInfo} = {};

		dependenciesIds.forEach((dependencyId) => {
			dependenciesInfo[dependencyId] = {
				fromDownloadManager: getState().downloadManager.items[dependencyId],
				fromDriveList: getState().driveList.driveItems[dependencyId],
			};
		});

		for (const dependencyId in dependenciesInfo) {
			if (dependenciesInfo.hasOwnProperty(dependencyId)) {
				const { fromDriveList, fromDownloadManager } = dependenciesInfo[dependencyId];

				if (fromDownloadManager !== undefined && fromDriveList !== undefined) { // prevent infinite loop when a dependency and its parent are mutually dependent of each other
					const newDependentsId = fromDownloadManager.dependents.filter((dependent) => dependent !== driveItem.id);
					dispatch(updateDownload(fromDriveList, fromDownloadManager.mode, fromDownloadManager.dependencies, newDependentsId));
				}
			}
		}
	};

const transformTemporaryDownload = (driveItem: Api.DriveItem, itemFromDownloadManager: DownloadManagerItemState, dependentId?: number) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<void> => {
		const fileName = `${driveItem.id}.${Utils.getFileExtension(driveItem.originalFile)}`;
		const { dependencies, dependents } = itemFromDownloadManager;
		const downloadMode = dependentId === undefined ? DownloadMode.Requested : DownloadMode.Dependency;

		let newDependents = [...dependents];
		if (dependentId !== undefined) {
			newDependents = [...dependents, dependentId];
		}

		return NativeApi.moveFile(`${tempFolder}/${fileName}`, `${downloadsFolder}/${fileName}`)
			.then((result) => {
				if (!result) {
					dispatch(createNotification('error.download_fail', { id: driveItem.id }));
				} else {
					dispatch(updateDownload(driveItem, downloadMode, dependencies, newDependents));
				}
			})
		;
	};

const getMostImportantMode = (mode1: DownloadMode, mode2: DownloadMode) => {
	const orderOfImportance = [DownloadMode.Temp, DownloadMode.Dependency, DownloadMode.Requested, DownloadMode.Required];

	return orderOfImportance.indexOf(mode1) > orderOfImportance.indexOf(mode2) ? mode1 : mode2;
};

const updateDependents = (driveItem: Api.DriveItem, itemFromDownloadManager: DownloadManagerItemState, requestedMode: DownloadMode, dependentId?: number) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<void> => {
		const { dependencies, dependents, mode } = itemFromDownloadManager;
		let newDependents = [...dependents];
		if (dependentId !== undefined && !dependents.includes(dependentId)) {
			newDependents.push(dependentId);
		}
		const newMode = getMostImportantMode(mode, requestedMode);
		dispatch(updateDownload(driveItem, newMode, dependencies, newDependents));
		return Q.Promise.resolve({});
	};

const recordDownloadProgress = (progress: Progress, id: number) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const { downloadManager } = getState();

	const lastProgress = downloadManager.items[id] ? downloadManager.items[id].progress : undefined;
	const lastRate = lastProgress && lastProgress.loaded && lastProgress.totalSize ? Math.round(lastProgress.loaded * 100 / lastProgress.totalSize) : 0;
	const currentRate = progress && progress.loaded && progress.totalSize ? Math.round(progress.loaded * 100 / progress.totalSize) : 0;

	if (lastRate !== currentRate) {
		dispatch({
			type: DownloadManagerActionKeys.RECORD_DOWNLOAD_PROGRESS,
			progress,
			id
		});
	}
};

const closeDownloadGroupToast = (): CloseDownloadGroupToastAction => ({
	type: DownloadManagerActionKeys.CLOSE_DOWNLOAD_GROUP_TOAST,
});

export {
	loadPreviousDownloads,
	updateDownloadInManager,
	addItem,
	updateDownloadStatus,
	updateDownload,
	removeDownloadItem,
	setRequestId,
	recordDownloadProgress,
	removeDependentFromDependencies,
	transformTemporaryDownload,
	updateDependents,
	startDownloadGroup,
	finishDownloadGroup,
	setIsDeletingAllStatus,
	setIsMarkingItemForDeletion,
	addItemInManager,
	setDownloadGroupStatus,
	closeDownloadGroupToast,
	addSelectedDownloadItem,
	removePendingSelectedDownloadItem,
	resetTotalSelectedDownloadsInManager,
	resetPendingSelectedDownloadsInManager,
	resetSelectedDownloadItems,
	resetDownloads,
	addDownloadInProgressItem,
	removeDownloadInProgressItem,
	addDownloadInProgressDependency,
	removeDownloadInProgressDependency,
	StoredDownloadGroups,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/DownloadManager/actions/store.ts